import type {
  GA4ImpressionItemsConfig,
  SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { APIBookingDraft, APIBookingDraftUserRedeemableBooking } from '@/scopes/checkout/infrastructure/api/types'
import { isNil } from 'ramda'
import {
  type CheckoutBookingRedeem,
  type CheckoutBookingRedeemerService,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import {
  getGA4CategoryByServiceType,
  mapDefaultSelectedItemToGA4,
} from '../model-mappers/ga4AnalyticsMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceType: ServiceType
}

export const createBookingRedeemServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutBookingRedeemerService> = (dependencies) => {
  const { serviceName, serviceTypeMapper, serviceType } = dependencies

  function map(bookingDraft: APIBookingDraft) {
    const { user } = bookingDraft
    if (!user) {
      return null
    }
    const selectedValue = mapSelectedValue(user?.redeemableBookings || [])
    return {
      id: 'BookingRedeemService',
      selectedValue,
      isSelected: !isNil(selectedValue),
      type: ServiceType.BookingRedeem,
      bookings: (user?.redeemableBookings || []).reduce(
        (acc2: Record<string, CheckoutBookingRedeem>, booking) => ({
          ...acc2,
          [booking.number]: mapRedeemableBooking(booking),
        }),
        {},
      ),
    }
  }

  function mapRedeemableBooking(redeemableBooking: APIBookingDraftUserRedeemableBooking) {
    if (!redeemableBooking) {
      return null
    }
    return {
      bookingNumber: redeemableBooking.number.toString(),
      description: redeemableBooking.description,
      date: redeemableBooking.date,
      disabled: !redeemableBooking.isRedeemable,
      paidAmount: redeemableBooking.paidAmount.value,
      composition: redeemableBooking.composition,
      location: `${redeemableBooking.location.country}, ${redeemableBooking.location.region}, ${redeemableBooking.location.city}`,
      accoImageUrl: redeemableBooking.accoImageUrl,
    }
  }

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function mapSelectedValue(redeemableBookings: APIBookingDraftUserRedeemableBooking[]) {
    if (!redeemableBookings) {
      return null
    }
    const selectedBooking = redeemableBookings.find(redeemableBooking => redeemableBooking.selected)
    return selectedBooking?.number.toString() || null
  }

  function mapSelection(service: CheckoutBookingRedeemerService) {
    return [{ value: service.selectedValue }]
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, _config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const redeemableBookings = bookingDraft.user?.redeemableBookings
    if (!redeemableBookings?.length)
      return []

    const ga4Category = getGA4CategoryByServiceType(serviceType, serviceName)
    return redeemableBookings.map(booking => ({
      item_name: booking.description,
      item_id: booking.number.toString(),
      price: booking.paidAmount.value,
      item_category: `Ancillary > Additional cost > ${ga4Category}`,
      item_category2: ga4Category,
      item_list_name: 'checkout1',
      quantity: '1',
    }))
  }

  function mapSelectedItemToGA4(service: CheckoutBookingRedeemerService, step: number): ImpressionViewAncillariesItem[] {
    if (!service.selectedValue)
      return []

    const booking = service.bookings?.[service.selectedValue]

    if (!booking)
      return []

    const selectedItemInput: SelectedItemInput = {
      label: booking.description,
      value: booking.bookingNumber,
      price: booking.paidAmount,
    }

    return mapDefaultSelectedItemToGA4([selectedItemInput], {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
